import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { setAlert } from "@/core/app/slices/alert/alertSlice"
import { setIsRouteChangeBlocked } from "@/core/app/slices/navigationPopup/navigationPopupSlice"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { getFormByName } from "@/core/app/slices/uassist/form.util"
import {
  deleteFormSavedData,
  fetchFormByCaseIdWithMergeData,
  saveDefaultFormData,
} from "@/core/app/slices/uassist/formThunkApi"
import { RootState } from "@/core/app/store"
import { parseToObject } from "@/core/utils/formatters"
import { useCallbackPrompt } from "@/hooks/useCallbackPrompt"
import { NoUassistPopup } from "@/modules/Uassist/USmartForms/NoUassistPopup"
import JsonFormBuilder from "@/rx-form/builder/JsonFormBuilder"
import {
  generateSummaryData,
  generateSummarySchema,
  generateSummaryUiSchema,
} from "@/rx-form/components/NewForm/formUtils"
import { UButton, UText } from "@/ui-component"
import { Box, Button, Grid } from "@mui/material"
import {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import NavigationBlockPopup from "../NavigationBlockPopup/NavigationBlockPopup"
import { useFormSave } from "./CustomHooks/useFormSave"
import { useSubmitUassist } from "./CustomHooks/useSubmitUassist"
import { FormProvider, useFormContext } from "./FormContext"
import { checkIfFormMetaIsValid, useDeepCompareEffect, isRetainerFastrack } from "./formUtils"
import { PdfContent, PdfContentRef } from "./SummaryPDF/PdfContent"
import { FormRenderMode, FormContentRef, FormContentProps, FormProps, FormType } from "./types"
import { ErrorBoundary } from "react-error-boundary"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import { ROUTE_ATTENTION_BANNER } from "@/routes/status.route.config"
import { setIsRecordsUpdated } from "@/core/app/slices/uassist/formSlice"
import { useMyNavigation } from "@/hooks/useMyNavigation"
import { setIsCloseTab } from "@/core/app/slices/patients/patientSlice"
import { matchPath } from "react-router-dom"
import { safeCloseWindow } from "@/core/app/CloseTabContext"

// Use the nested form name as the accordion heading when available.
const getNestedAccordionLabel = (formName?: string | null, schema?: any) =>
  formName

// Convert the nested form root layout into the existing accordion renderer contract.
const wrapNestedFormUiSchemaInAccordion = (
  uiSchema: any,
  accordionLabel: string,
) => {
  if (!uiSchema || typeof uiSchema !== "object") return uiSchema
  if (uiSchema.type === "AccordionGroup") return uiSchema

  return {
    ...uiSchema,
    type: "AccordionGroup",
    label: accordionLabel,
    elements: Array.isArray(uiSchema.elements) ? uiSchema.elements : [],
    mode: FormRenderMode.EDIT
  }
}


const enrichNestedFormSchema = (schema: any, accordionLabel: string) => {
  if (!schema || typeof schema !== "object" || schema.title) return schema

  return {
    ...schema,
    title: accordionLabel,
  }
}

/**
 * Main form content component. Handles form state, validation, and submission logic.
 * Uses React.forwardRef to expose imperative methods.
 */
const FormContent: ForwardRefRenderFunction<
  FormContentRef,
  FormContentProps
> = (props, ref) => {
  const {
    details = undefined,
    onChange,
    onFormSaved,
    setCurrentFormType,
    unlockFunc,
    onSavingChange,
  } = props
  const {
    formType,
    formName,
    data,
    schema,
    uiSchema,
    saveId,
    setSaveId,
    setFormType,
    setFormName,
    setData,
    setSchema,
    setUiSchema,
    showValidationErrors,
    setShowValidationErrors,
    mode,
    renderedTemplate,
    routeBackToForm,
    nestedFormRefs,
    setUiSchemaVersion,
  } = useFormContext()
  const dispatch = useAppDispatch()
  const navigate = useMyNavigation()
  const { t } = useTranslation("common")
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { clinicalSettings } = useAppSelector(
    (state: RootState) => state.userService,
  )
  const { handleAttentionNeededFormSave, handleNewPatientFormSave } = useFormSave()
  const { handleAttentionNeededSubmit, handleCaseSubmit } = useSubmitUassist()

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [currentDisplayMeta, setCurrentDisplayMeta] = useState<
    { formTitle?: string | null; formDescription?: string | null } | undefined
  >(undefined)

  const [noUassistFormSubmitted, setNoUassistFormSubmitted] =
    useState<boolean>(false)

  const { formTemplateList } = useAppSelector(
    (state: RootState) => state.formService,
  )

  const [showTemplateNavBlockPopup, setShowTemplateNavBlockPopup] =
    useState<boolean>(false)

  const [blockRouteForTemplateView, setBlockRouteForTemplateView] =
    useState<boolean>(false)

  const { isRouteChangeBlocked } = useAppSelector(
    (state: RootState) => state.navigationPopup,
  )

  const { isCloseTab } = useAppSelector(
    (state: RootState) => state.patientService,
  )

  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(isRouteChangeBlocked)
  const [showNoUassistAlert, setShowUNoUassistAlert] = useState<boolean>(false)

  const pdfContentRef = useRef<PdfContentRef>(null)

  /**
   * Shows an alert message using the global alert system.
   */
  const popAlert = (message: string, isError: boolean = false) => {
    dispatch(
      setAlert({
        message: message,
        isError: isError,
      }),
    )
  }


  const removeApprovalGroup = (uiSchema: any) => {
    if (!uiSchema.elements) return uiSchema;

    return {
      ...uiSchema,
      elements: uiSchema.elements
        // remove the Approval (Optional) group
        .filter(
          (el) =>
            !(
              el.type === "Group" &&
              el.label === "Approval (Optional)"
            )
        )
        // recursively process nested elements
        .map((el) => removeApprovalGroup(el)),
    };
  }

  /**
   * Marks the final_retainer field as disabled if case_disposition is "ATTENTION_NEEDED"
   * for refinement forms.
   */
  const markFinalRetainerAsReadOnly = (schema: any, formName: string) => {
    const normalizedFormName = formName?.toLowerCase() || ""
    const isRefinement = [
      FORM_CONSTANT.REFINEMENT.toLowerCase(),
      FORM_CONSTANT.REFINEMENT_2.toLowerCase(),
    ].includes(normalizedFormName)

    // Only apply to refinement forms when case_disposition is ATTENTION_NEEDED
    if (!isRefinement) {
      return schema
    }

    // Mark final_retainer field as read-only
    if (schema?.properties?.final_retainer) {
      return {
        ...schema,
        properties: {
          ...schema.properties,
          final_retainer: {
            ...schema.properties.final_retainer,
            isRadioGroupReadOnly: true,
          },
        },
      }
    }

    return schema
  }

 /**
   * Sets the form meta (schema, uiSchema, data, etc.) to context from API response.
   */
  function setFormMetaToContext(response: any) {
    const isAttentionNeeded = matchPath("/overview/patient/:patientId/case/:caseId/attentionBanner/RxForm", location.pathname)

    // Determine which uischema to use
    let uiSchemaToSet: any
    if(isAttentionNeeded){
      // Use retainerV2 uischema without the approval group
      const parsedUiSchema = parseToObject(response?.ui_schema?.schema_data) || {}
      uiSchemaToSet = removeApprovalGroup(parsedUiSchema)
    } else {
      uiSchemaToSet = parseToObject(response?.ui_schema?.schema_data) || {}
    }

    // Set all form metadata (common code)
    setUiSchema(uiSchemaToSet)
    let schemaToSet = parseToObject(response?.form_schema?.schema_data) || {}

    const formName = response?.form_schema?.name
    if (isAttentionNeeded) {
      schemaToSet = markFinalRetainerAsReadOnly(schemaToSet, formName)
    }

    setSchema(schemaToSet)
    const defaultData = parseToObject(response?.default_data) || {}
    if(isAttentionNeeded){
      defaultData.approval = 'yes'
    }
    setData(defaultData)
    setFormName(formName)
    setFormType(response?.form_schema?.type)
    setSaveId(response?.save_id || null)
    setUiSchemaVersion(response?.ui_schema?.version || null)

    if (typeof details?.displayMeta === "function") {
      setCurrentDisplayMeta(details.displayMeta(response?.form_schema?.name))
    } else {
      setCurrentDisplayMeta(details?.displayMeta)
    }
  }

  /**
   * Submits the current form data to Uassist, handling PDF export and API calls.
   * Shows an alert on error.
   */
  const submitToUassist = async (): Promise<void> => {
    dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))

    if (!formName) {
      // Defensive guard – should never happen in normal flows, but avoids
      // runtime TypeErrors and gives a clearer error surface than "undefined".
      popAlert(t("uassist.uAssistForm.uassistSubmitFailed"), true)
      return
    }

    const normalizedFormName = formName.toLowerCase()
    const isRefinement = [
      FORM_CONSTANT.REFINEMENT.toLowerCase(),
      FORM_CONSTANT.REFINEMENT_2.toLowerCase(),
    ].includes(normalizedFormName)
    const isRetainer =
      normalizedFormName === FORM_CONSTANT.RETAINER.toLowerCase()

    let errorMessage = isRefinement
      ? t("uassist.uAssistForm.refinementsavefailed")
      : t("uassist.uAssistForm.uassistSubmitFailed")
    try {
      const response: any = await pdfContentRef.current?.exportComponentAsPDF(
        pdfContentRef.current?.contentToPDF,
      )
      if (
        !data?.approval ||
        !isRetainerFastrack(isRetainer, isRefinement, data) ||
        matchPath(
          `/overview/patient/:patientId/case/:caseId/attentionBanner/RxForm`,
          location.pathname,
        )
      ) {
        if (response && (response as any).payload?.status === "Success") {
          location.pathname.includes(
            `${ROUTE_ATTENTION_BANNER.attentionBanner}/RxForm`,
          )
            ? await handleAttentionNeededSubmit(isRefinement)
            : await handleCaseSubmit({
                details: { ...details, formName },
                data,
                formTemplateList,
                formName,
                unlockFunc,
              })
        } else {
          throw new Error(errorMessage)
        }
      } else {
        const params = new URLSearchParams(location.search)
        params.set("isRetainerFastrack", "true")
        if (
          matchPath(
            "/clinical/patient/:patientId/case/:caseId/treat",
            location.pathname,
          )
        ) {
          params.set("from", "clinical")
          if (isRefinement) {
            params.set("isRefinement", "true")
          }
        } else if (
          matchPath(
            `/records/patient/:patientId/case/:caseId/rxForm/prescription`,
            location.pathname,
          )
        ) {
          params.set("from", "patientRecords")
        }
        navigate(
          `/order/patient/${details?.patientId?.toString()}/case/${details?.caseId?.toString()}/bundle?${params.toString()}`,
        )
        return
      }
    } catch (error) {
      popAlert(errorMessage, true)
    }
  }

  /**
   * Saves the SmartRx form data.
   */
  const saveSmartRxForm = async () => {
    try {
      const formMeta =
        details?.formType && details?.formName
          ? getFormByName(
              formTemplateList,
              details?.formType,
              details?.formName,
            )
          : null

      if (!formMeta || !formMeta?.id) {
        popAlert(t(`uassist.uAssistForm.dataSaveStatusFailure`), true)
        return
      }

      await dispatch(
        saveDefaultFormData({
          formId: formMeta.id,
          payload: data,
        }),
      ).then((res: any) => {
        onFormSaved && onFormSaved(false)
        if (res.payload.status?.toLowerCase() === "success") {
          popAlert(t(`uassist.uAssistForm.dataSaveStatusSuccess`))
        } else {
          popAlert(t(`uassist.uAssistForm.dataSaveStatusFailure`), true)
        }
      })
    } catch (err) {
      console.error("saveSmartRxForm error:", err)
      popAlert(t(`uassist.uAssistForm.dataSaveStatusFailure`), true)
    }
  }

  /**
   * Resets the form data to its default state.
   */
  const resetFormData = async () => {
    if (!details?.formType || !details?.formName) return
    const formMeta = getFormByName(
      formTemplateList,
      details.formType,
      details.formName,
    )
    const formId = formMeta?.id
    if (!formId) {
      popAlert(t(`uassist.uAssistForm.dataResetStatusFailure`), true)
      return
    }
    dispatch(deleteFormSavedData({ formId: formId })).then((res: any) => {
      if (res?.payload?.status === 200) {
        dispatch(
          fetchFormByCaseIdWithMergeData({
            formId,
            caseId: details?.caseId?.toString(),
            getSavedData: details.fetchSavedData || false,
            patientId: details.patientId?.toString(),
            formType: details.formType,
            uassistRound: details.uassistRound || 0,
          })
        ).then((response: any) => {
          setFormMetaToContext(response?.payload)
        })
      } else {
        popAlert(t(`uassist.uAssistForm.dataResetStatusFailure`), true)
      }
    })
  }

  /**
   * Handles changes to the form data.
   */
  const handleChange = (newData: Record<string, any>) => {
    // Conditionally call setData based on transition state
    if (!renderedTemplate?.isTransitioning) {
      setData(newData);
    }
    if( renderedTemplate?.showTemplate && !blockRouteForTemplateView ){
      // for template view, we block the route change on data change
      setBlockRouteForTemplateView(true)
    }
    if ( !renderedTemplate?.showTemplate && onChange) {
      // Call onChange if provided, passing the new data
      // Dont pass template data to parent callback.
      onChange(newData)
    }
  }

  /**
   * Saves the form data for attention needed and new patient forms.
   */
  const saveFormData = async () => {
    try {
      location.pathname.includes(
        `${ROUTE_ATTENTION_BANNER.attentionBanner}/RxForm`,
      )
        ? await handleAttentionNeededFormSave({
            formType,
            formName,
            data,
            caseDetail,
            clinicalSettings,
            saveId,
            popAlert,
            setShowUNoUassistAlert,
          })
        : await handleNewPatientFormSave({
            formType,
            formName,
            data,
            saveId,
            details,
            popAlert,
          })
    } catch (error) {
      // Normalize unknown rejections so downstream consumers and logging
      // never see a bare "undefined" error message.
      if (error instanceof Error) {
        throw error
      }
      throw new Error("Unknown error while saving form data")
    }
  }

  /**
   * Validates nested forms first, then validates the main form and handles saving.
   * Returns true if validation passes, false otherwise.
   */
  const validateErrors = async (): Promise<boolean> => {
    if (isSaving) return Promise.resolve(false)
    setIsSaving(true)
    onSavingChange && onSavingChange(true)
    return new Promise((resolve) => {
      setTimeout(async () => {
        // First, validate all nested forms
        if (nestedFormRefs && nestedFormRefs.size > 0) {
          const nestedValidationPromises = Array.from(nestedFormRefs.values()).map(
            (nestedRef) => nestedRef.validate()
          )
          
          const nestedResults = await Promise.all(nestedValidationPromises)
          const allNestedValid = nestedResults.every((result) => result === true)
          
          if (!allNestedValid) {
            // At least one nested form failed validation
            resolve(false)
            setIsSaving(false)
            onSavingChange && onSavingChange(false)
            return
          }
        }

        // Now validate the main form
        const errorElms = document.getElementsByClassName("Mui-error")
        if (errorElms.length) {
          setTimeout(() => {
            (errorElms[0] as HTMLElement)?.scrollIntoView({ behavior: "smooth" })
          }, 100)
          resolve(false)
          setIsSaving(false)
          onSavingChange && onSavingChange(false)
          return
        } else {
          if (details?.isSettings) {
            saveSmartRxForm()
            // once form is saved successfully, then disable nav block 
            setBlockRouteForTemplateView(false)
            dispatch(setIsRouteChangeBlocked({ isRouteChangeBlocked: false }))
            resolve(true)
            setIsSaving(false)
            onSavingChange && onSavingChange(false)
          } else {
            saveFormData()
              .then(async () => {
                if (renderedTemplate?.showTemplate) {
                  routeBackToForm(true)
                  setShowValidationErrors(false)
                  dispatch(setIsRecordsUpdated(true))
                  popAlert(t("uassist.uAssistForm.uassistDataSaveStatusSuccess"))
                } else if (onFormSaved) {
                  onFormSaved(true)
                }
                resolve(true)
                setIsSaving(false)
                onSavingChange && onSavingChange(false)
              })
              .catch((error) => {
                console.error("Form Data Save error : ", error)
                setShowValidationErrors(false)
                popAlert(
                  t("uassist.uAssistForm.prescriptionSaveStatusError"),
                  true,
                )
                resolve(false)
                setIsSaving(false)
                onSavingChange && onSavingChange(false)
              })
          }
        }
      }, 100)
    })
  }

  useDeepCompareEffect(() => {
    // Initial load
    // fetch all form details and populate context
    if (formTemplateList.length === 0 || !details) return
    
    setIsFetching(true)

    const formId: string | null =
      details.formId ||
      (details.formType && details.formName
        ? getFormByName(formTemplateList, details.formType, details.formName).id
        : null)
    dispatch(
      fetchFormByCaseIdWithMergeData({
        formId,
        caseId: details?.caseId?.toString(),
        getSavedData: details?.fetchSavedData || false,
        patientId: details?.patientId?.toString(),
        formType: details?.formType,
        uassistRound: details?.uassistRound || 0,
      }),
    ).then((response: any) => {
      if (!response?.payload) {
        setNoUassistFormSubmitted(true)
      } else {
        setFormMetaToContext(response?.payload)
        setNoUassistFormSubmitted(false)
        setShowValidationErrors(false)
      }
    }).finally(()=>{
      setIsFetching(false)
    })
  }, [formTemplateList, details])

  useEffect(() => {
    setCurrentFormType && setCurrentFormType(formType)
  }, [formType])

  useEffect(() => {
    if (!showValidationErrors) return
    validateErrors()
  }, [showValidationErrors])

  useDeepCompareEffect(()=>{
    // Reset Nav block flags for Templte when renderedTemplate.showTemplate changes
    setShowTemplateNavBlockPopup(false)
    setBlockRouteForTemplateView(false)
  },[renderedTemplate?.showTemplate])
  
  useImperativeHandle(ref, () => ({
    saveFormData,
    validateErrors,
    setShowValidationErrors,
    showValidationErrors,
    submitToUassist,
    data,
    saveSmartRxForm,
    formName,
    resetFormData,
    exportComponentAsPDF: (status: "open" | "upload") => {
      pdfContentRef.current?.exportComponentAsPDF(
        pdfContentRef.current?.contentToPDF,
        status,
      )
    }
  }))

  const nestedAccordionLabel = useMemo(
    () => getNestedAccordionLabel(`${formName} Template`),
    [formName]
  )

  const renderedSchema = useMemo(() => {
    // Build the same schema as before, then add a title only for embedded child forms.
    const baseSchema =
      mode === FormRenderMode.SUMMARY
        ? generateSummarySchema(
            schema,
            renderedTemplate?.schema || {},
          )
        : schema

    return details?.isNestedForm
      ? enrichNestedFormSchema(baseSchema, nestedAccordionLabel)
      : baseSchema
  }, [
    details?.isNestedForm,
    mode,
    nestedAccordionLabel,
    renderedTemplate?.schema,
    schema,
  ])

  const renderedUiSchema = useMemo(() => {
    const baseUiSchema =
      mode === FormRenderMode.SUMMARY
        ? generateSummaryUiSchema(
            uiSchema,
            data,
            renderedTemplate?.uiSchema || {},
            false,
          )
        : uiSchema

    return details?.isNestedForm
      ? wrapNestedFormUiSchemaInAccordion(baseUiSchema, nestedAccordionLabel)
      : baseUiSchema
  }, [
    data,
    details?.isNestedForm,
    mode,
    nestedAccordionLabel,
    renderedTemplate?.uiSchema,
    uiSchema,
  ])

  return (
    <Box>
      <NoUassistPopup
        showPopup={showNoUassistAlert}
        setShowPopup={setShowUNoUassistAlert}
        setIsPdfLoading={setShowValidationErrors}
      />
      <Grid
        container
        flexDirection={"column"}
        sx={{
          backgroundColor: "background.default",
          borderRadius: 2,
          // padding: 5,
          justifyContent: "start",
        }}
      >
        {!noUassistFormSubmitted &&
          (currentDisplayMeta?.formTitle ||
            currentDisplayMeta?.formDescription) && (
            <Grid
              item
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                width: "100%",
                mb: "16px",
              }}
            >
              {((renderedTemplate?.showTemplate &&
                !!renderedTemplate?.template) ||
                (!renderedTemplate?.showTemplate &&
                  currentDisplayMeta?.formTitle)) && (
                <UText variant={"h4"} sxProp={{ textAlign: "center", mb: 1 }}>
                  {renderedTemplate?.showTemplate
                    ? renderedTemplate?.template
                    : currentDisplayMeta?.formTitle}
                </UText>
              )}
              {!renderedTemplate?.showTemplate &&
                currentDisplayMeta?.formDescription && (
                  <UText
                    variant={"body2"}
                    sxProp={{ textAlign: "center", color: "rgb(0,0,0,0.6)" }}
                  >
                    {currentDisplayMeta?.formDescription}
                  </UText>
                )}
            </Grid>
          )}
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
          >
            
          {!isFetching && (!noUassistFormSubmitted &&
            details &&
            checkIfFormMetaIsValid(schema, uiSchema, data) ? (
              <>
                <JsonFormBuilder
                  schema={renderedSchema}
                  uischema={renderedUiSchema}
                  defaultData={
                    mode === FormRenderMode.SUMMARY
                      ? generateSummaryData({ ...data, ...renderedTemplate?.data })
                      : data
                  }
                  handleChange={handleChange}
                  isFormSubmitted={showValidationErrors}
                  setIsFormSubmitted={setShowValidationErrors}
                />
                {mode === FormRenderMode.SUMMARY && (
                  <PdfContent ref={pdfContentRef} displayMeta={currentDisplayMeta} uassistRound={details.uassistRound || 0} />
                )}
              </>
            ) : (
              <UText
                variant="body2"
                sxProp={{ textAlign: "center", color: "text.secondary" }}
              >
                {t("uassist.noUassistFormSubmitted")}
              </UText>
            ))}
          {renderedTemplate?.showTemplate && (
            <Box
              sx={{
                width: "100%",
                backgroundColor: "background.default",
                padding: 2,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <UButton
                variant="outlined"
                onClickHandler={() => {
                  // If route is blocked, show nav block popup.
                  // Let user decide on navigation.
                  // If route is not blocked, simple route away from template form.
                  if(blockRouteForTemplateView) {
                    setShowTemplateNavBlockPopup(true)
                  }else{
                    routeBackToForm(false)
                  }
                }}
                btnText="Back"
                startIcon={<ChevronLeftIcon fontSize={"small"} sx={{ marginRight: "3px" }} />}
              />
              <UButton
                variant="contained"
                onClickHandler={() => {
                  setShowValidationErrors(true)
                  if (showValidationErrors) {
                    validateErrors()
                  }
                }}
                btnText="SAVE FOR THIS CASE"
              />
            </Box>
          )}
        </Grid>
      </Grid>
      {!details?.isNestedForm && <NavigationBlockPopup
        cancelNavigation={()=>{
          if(matchPath('/records/patient/:patientId/case/:caseId/rxForm/:subroute/', location.pathname) && isCloseTab){
            // for new patient flow close tab action
            dispatch(setIsCloseTab(false))
          }
          cancelNavigation()
        }}
        confirmNavigation={()=>{
          if(matchPath('/records/patient/:patientId/case/:caseId/rxForm/:subroute/', location.pathname) && isCloseTab){
            // for new patient flow close tab action
            safeCloseWindow()
            return
          }
          confirmNavigation()
        }}
        showPrompt={showPrompt}
      />}
      {renderedTemplate?.showTemplate && (
        // Nav Block for template. Based on data change. 
        // Independent of route change.
        <NavigationBlockPopup
          cancelNavigation={()=>setShowTemplateNavBlockPopup(false)}
          confirmNavigation={()=>routeBackToForm(false)}
          showPrompt={showTemplateNavBlockPopup}
        />
      )}
    </Box>
  )
}

const FormContentWithRef = forwardRef(FormContent)
FormContentWithRef.displayName = "FormContent"

/**
 * Main Form component. Wraps FormContent with FormProvider and exposes ref methods.
 */
const FormComponent: ForwardRefRenderFunction<FormContentRef, FormProps> = (
  props,
  ref,
) => {
  const {
    details = undefined,
    mode = FormRenderMode.EDIT,
    onChange,
    showValidationErrors = false,
    onFormSaved,
    setCurrentFormType,
    unlockFunc,
    onSavingChange,
  } = props

  const fallbackRender = ({ error, resetErrorBoundary }: { error: Error, resetErrorBoundary: () => void }) => (
    <Box sx={{ py: 32, textAlign: "center" }}>
      <p>Something went wrong in the form.</p>
      <Button onClick={resetErrorBoundary} sx={{ padding: "8px 16px", fontSize: 16 }}>
        Retry
      </Button>
    </Box>
  )

  const handleSavingChange = (saving: boolean) => {
    if (onSavingChange) onSavingChange(saving)
  }

  return (
    <ErrorBoundary fallbackRender={fallbackRender}>
      <FormProvider
        details={details}
        mode={mode}
        showValidationErrors={showValidationErrors}
      >
        <FormContentWithRef
          ref={ref}
          details={details}
          onChange={onChange}
          onFormSaved={onFormSaved}
          setCurrentFormType={setCurrentFormType}
          unlockFunc={unlockFunc}
          onSavingChange={handleSavingChange}
        />
      </FormProvider>
     </ErrorBoundary>
  )
}

export const Form = forwardRef(FormComponent)
Form.displayName = "Form"

export default Form